import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMatch } from 'react-router-dom-v5-compat';

import { getTurnTrackingPartiesNames } from '~/components/Workflow/TicketsViewPage/utils';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { Nullable } from '~/types/Nullable';
import {
  CompletedTurnTrackingHistoryType,
  TurnTrackingHistoryType,
} from '~/types/TurnTracking';
import { Uuid } from '~/types/Uuid';

import { EditCyclePanel } from './EditCyclePanel';
import { PartySelectField } from './PartySelectField';
import { ProgressBar } from './ProgressBar';
import { TurnTrackingHistory } from './TurnTrackingHistory';
import {
  isEntryUpdated,
  partyNameFromPartyIdAndParties,
  timeFieldErrors,
} from './utils';

interface Props {
  ticketId: Uuid;
  workflowId: Uuid;
  mode?: 'inline' | 'floating';
  isHidden?: boolean;
  onHide?: () => void;
}

export const TurnTrackingPanel = ({
  ticketId,
  workflowId,
  mode,
  isHidden,
  onHide,
}: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [isCurrentTurn, setIsCurrentTurn] = useState(false);
  const [entryDraft, setEntryDraft] =
    useState<Nullable<TurnTrackingHistoryType>>(null);
  const [entryReference, setEntryReference] =
    useState<Nullable<TurnTrackingHistoryType>>(null);
  const [showInitiateResponsibleParty, setShowInitiateResponsibleParty] =
    useState(false);
  const [isCreatingNewEntry, setIsCreatingNewEntry] = useState(false);
  const [partyId, setPartyId] = useState<Nullable<Uuid>>(null);
  const isTicketList = Boolean(useMatch(RoutePathType.WorkflowTickets));

  const [createEntry, createEntryResult] =
    api.endpoints.createTurnTrackingEntry.useMutation();
  const { isLoading: isCreateEntryLoading } = createEntryResult;
  const [updateEntry, updateEntryResult] =
    api.endpoints.updateTurnTrackingEntry.useMutation();
  const { isLoading: isUpdateEntryLoading } = updateEntryResult;

  const isLoadingActionButton = isCreateEntryLoading || isUpdateEntryLoading;

  const {
    data: historyData = [],
    isError: historyError,
    isLoading: isLoadingHistory,
  } = api.endpoints.getTurnTrackingHistory.useQuery({
    ticketId,
  });

  const {
    data: partiesDataRaw = [],
    isError: partiesError,
    isLoading: isLoadingParties,
  } = api.endpoints.getTurnTrackingParties.useQuery({ workflowId });

  const partiesNames = getTurnTrackingPartiesNames(intl);
  const partiesData = partiesDataRaw.map((party) => ({
    ...party,
    name: partiesNames[party.name] ?? party.name, // Fallback to original name if not found
  }));

  // ticket name needed on TicketList, may move call there after workflow parties discussion
  const { data: ticketData, isLoading: isLoadingTicketData } =
    api.endpoints.getTicketSummary.useQuery({
      ticketId,
    });

  useEffect(() => {
    if (historyError) {
      toast({
        message: intl.formatMessage({
          id: 'TurnTrackingPanel.historyErrorMessage',
          defaultMessage: 'Try again in a few minutes.',
          description: 'Error message when history fails to load',
        }),
        title: intl.formatMessage({
          id: 'TurnTrackingPanel.historyErrorTitle',
          defaultMessage: "We're experiencing difficulty loading History.",
          description: 'Error title when history fails to load',
        }),
        status: 'danger',
      });
    }
  }, [historyError]);

  useEffect(() => {
    if (partiesError) {
      toast({
        message: intl.formatMessage({
          id: 'TurnTrackingPanel.partiesErrorMessage',
          defaultMessage: 'Try again in a few minutes.',
          description: 'Error message when parties fail to load',
        }),
        title: intl.formatMessage({
          id: 'TurnTrackingPanel.partiesErrorTitle',
          defaultMessage:
            "We're experiencing difficulty loading Responsible Parties.",
          description: 'Error title when parties fail to load',
        }),
        status: 'danger',
      });
    }
  }, [partiesError]);

  const onBack = () => {
    setShowEditPanel(false);
    setEntryDraft(null);
    setEntryReference(null);
    setShowInitiateResponsibleParty(false);
    setIsCreatingNewEntry(false);
  };

  const handleOnSubmit = async () => {
    try {
      if (!isCreatingNewEntry && !showInitiateResponsibleParty) {
        await updateEntry({ ticketId, entry: entryDraft! }).unwrap();
        onBack();
        toast({
          message: intl.formatMessage({
            id: 'TurnTrackingPanel.cycleTimeUpdated',
            defaultMessage: 'Cycle time updated',
            description: 'Success message for cycle time update',
          }),
          status: 'success',
        });
      } else {
        await createEntry({
          ticketId,
          partyId: showInitiateResponsibleParty
            ? partyId!
            : entryDraft!.partyId,
        }).unwrap();
        onBack();
        const message = intl.formatMessage(
          {
            id: 'TurnTrackingPanel.responsiblePartyUpdated',
            defaultMessage:
              'Current responsible party updated{ticketListSuffix}',
            description:
              'Success message for responsible party update. Example: ticketListSuffix = " for Contract Review Task"',
          },
          {
            ticketListSuffix: isTicketList
              ? intl.formatMessage(
                  {
                    id: 'TurnTrackingPanel.ticketListSuffix',
                    defaultMessage:
                      ' for "{ticketName}". It will take a few seconds for changes to be reflected on Tickets list.',
                    description:
                      'Additional text for ticket list context. Example: ticketName = "Contract Review Task"',
                  },
                  { ticketName: ticketData?.name },
                )
              : '',
          },
        );
        toast({
          message,
          status: 'success',
        });
      }
    } catch (error: any) {
      toast({
        title: intl.formatMessage({
          id: 'TurnTrackingPanel.errorTitle',
          defaultMessage: 'Sorry, something has gone wrong',
          description: 'Generic error title',
        }),
        message: error?.response?.data?.data,
        status: 'danger',
      });
    }
  };

  const isTicketFinished =
    !!ticketData?.isCancelled || !!ticketData?.isCompleted;

  const currentPartyId = historyData[0]?.partyId;
  const currentPartyName = partyNameFromPartyIdAndParties(
    currentPartyId,
    partiesData,
  );

  const errorMessages = timeFieldErrors({
    turn: entryDraft,
    createdTime: ticketData?.createdDate,
    intl,
  });
  const disableSave =
    !isEntryUpdated(entryDraft, entryReference) ||
    !entryDraft?.partyId ||
    errorMessages.length > 0;

  const previousEntry = historyData.filter((turn, index) => {
    if (!entryDraft) return null;
    const nextEntry = historyData[index - 1];
    return nextEntry?.turnId === entryDraft.turnId;
  })[0];
  const nextEntry = historyData.filter((turn, index) => {
    if (!entryDraft) return null;
    const previousEntry = historyData[index + 1];
    return previousEntry?.turnId === entryDraft.turnId;
  })[0];

  const previousEndTime = previousEntry?.endTime || null;
  previousEndTime?.setSeconds(0);
  previousEndTime?.setMilliseconds(0);
  const subsequentStartTime = nextEntry?.startTime || null;
  subsequentStartTime?.setSeconds(0);
  subsequentStartTime?.setMilliseconds(0);

  entryDraft?.startTime?.setSeconds(0);
  entryDraft?.startTime?.setMilliseconds(0);
  entryDraft?.endTime?.setSeconds(0);
  entryDraft?.endTime?.setMilliseconds(0);

  const timesOverlap =
    (previousEndTime &&
      entryDraft?.startTime &&
      entryDraft.startTime < previousEndTime) ||
    (subsequentStartTime &&
      entryDraft?.endTime &&
      entryDraft.endTime > subsequentStartTime);
  if (timesOverlap) {
    errorMessages.push(
      intl.formatMessage({
        id: 'TurnTrackingPanel.timesOverlapError',
        defaultMessage:
          'Start and end times should not overlap with other turns for this ticket.',
        description: 'Error message for overlapping times',
      }),
    );
  }

  const filterCompleted = (
    turnTracking: TurnTrackingHistoryType,
  ): turnTracking is CompletedTurnTrackingHistoryType => !!turnTracking.endTime;

  const renderLoadingState = () => {
    return (
      <Layout direction="column" spacing={4}>
        <Layout h="40px" mt={4}>
          <LoadingShimmer />
        </Layout>
        <Layout h="40px">
          <LoadingShimmer />
        </Layout>
        <Layout h="40px">
          <LoadingShimmer />
        </Layout>
      </Layout>
    );
  };

  const renderInitiateResponsibleParty = () => {
    return (
      <PartySelectField
        onChange={(partyId) => setPartyId(partyId)}
        parties={partiesData}
      />
    );
  };

  const renderEmptyState = () => {
    return (
      <Layout
        direction="column"
        justify="center"
        align="center"
        h="100%"
        spacing={4}
      >
        {isTicketFinished ? (
          <Text variant="body-bold">
            {intl.formatMessage({
              id: 'TurnTrackingPanel.noCycleTimesTracked',
              defaultMessage: 'There were no cycle times entries tracked.',
              description:
                'Message when no cycle times were tracked for finished ticket',
            })}
          </Text>
        ) : (
          <>
            <Layout direction="column">
              <Text variant="body-bold">
                {intl.formatMessage({
                  id: 'TurnTrackingPanel.noCycleTimeEntries',
                  defaultMessage:
                    'There are no cycle time entries for this document.',
                  description: 'Message when no cycle time entries exist',
                })}
              </Text>
              <Text>
                {intl.formatMessage({
                  id: 'TurnTrackingPanel.setResponsiblePartyPrompt',
                  defaultMessage:
                    'Set a currently responsible party to create an entry.',
                  description: 'Instruction to set responsible party',
                })}
              </Text>
            </Layout>
            <Button
              variant="primary"
              text={intl.formatMessage({
                id: 'TurnTrackingPanel.setResponsiblePartyButton',
                defaultMessage: 'Set Responsible Party',
                description: 'Button text to set responsible party',
              })}
              onClick={() => {
                setShowInitiateResponsibleParty(true);
              }}
            />
          </>
        )}
      </Layout>
    );
  };

  const renderPanelContent = () => {
    return (
      <>
        {isTicketList && (
          <Layout direction="column" m={2}>
            <Text variant="tiny" color="text.secondary">
              {intl.formatMessage({
                id: 'TurnTrackingPanel.ticketNameLabel',
                defaultMessage: 'Ticket name',
                description: 'Label for ticket name',
              })}
            </Text>
            <Text variant="body-bold">{ticketData?.name}</Text>
          </Layout>
        )}
        <Layout>
          <Layout direction="column" m={2}>
            <Text variant="tiny" color="text.secondary">
              {intl.formatMessage({
                id: 'TurnTrackingPanel.currentlyWithLabel',
                defaultMessage: 'Currently with',
                description: 'Label for currently responsible party',
              })}
            </Text>
            <Text variant="body-bold">
              {currentPartyName ??
                intl.formatMessage({
                  id: 'TurnTrackingPanel.notSetLabel',
                  defaultMessage: 'Not Set',
                  description: 'Label when no party is set',
                })}
            </Text>
          </Layout>
          {!isTicketFinished && (
            <ActionsMenu
              actions={[
                {
                  label: intl.formatMessage({
                    id: 'TurnTrackingPanel.editAction',
                    defaultMessage: 'Edit',
                    description: 'Edit action label',
                  }),
                  value: 'Edit',
                },
              ]}
              name="more actions"
              onActionClick={() => {
                setIsCurrentTurn(true);
                setShowEditPanel(true);
                const latestEntry = cloneDeep(historyData[0]);
                setEntryDraft(latestEntry);
                // clone entry to avoid editing comparison reference
                setEntryReference(cloneDeep(latestEntry));
              }}
            />
          )}
        </Layout>
        <Layout direction="column" m={3}>
          <ProgressBar history={historyData} parties={partiesData} />
        </Layout>
        {historyData.length >= 2 ? (
          <Layout direction="column" m={2}>
            <Layout mb={'8px'} mt={'8px'}>
              <Text variant="section">
                {intl.formatMessage({
                  id: 'TurnTrackingPanel.historySection',
                  defaultMessage: 'History',
                  description: 'History section header',
                })}
              </Text>
            </Layout>
            <Layout direction="column" spacing={2}>
              {historyData
                .filter(filterCompleted)
                .map((historyEntry: CompletedTurnTrackingHistoryType) => {
                  return (
                    <TurnTrackingHistory
                      parties={partiesData}
                      historyEntry={historyEntry}
                      isTicketFinished={isTicketFinished}
                      onShowEditPanel={() => {
                        setIsCurrentTurn(false);
                        setShowEditPanel(true);
                      }}
                      onSetEditingEntry={(entry) => {
                        setEntryDraft(entry);
                        // clone entry to avoid editing comparison reference
                        setEntryReference(cloneDeep(entry));
                      }}
                    />
                  );
                })}
            </Layout>
          </Layout>
        ) : null}
      </>
    );
  };

  const renderingLogic = () => {
    if (isLoadingHistory || isLoadingParties || isLoadingTicketData) {
      return renderLoadingState();
    } else if (showInitiateResponsibleParty) {
      return renderInitiateResponsibleParty();
    } else if (historyData.length === 0) {
      return renderEmptyState();
    } else {
      return renderPanelContent();
    }
  };

  if (showEditPanel && entryDraft && entryReference) {
    return (
      <EditCyclePanel
        history={historyData}
        parties={partiesData}
        disableSave={disableSave}
        entry={entryDraft!}
        entryReference={entryReference!}
        previousEntry={previousEntry}
        nextEntry={nextEntry}
        errorMessages={errorMessages}
        isCurrentTurn={isCurrentTurn}
        isLoadingActionButton={isLoadingActionButton}
        isCreatingNewEntry={isCreatingNewEntry}
        isTicketFinished={isTicketFinished}
        {...(isHidden && { isHidden })}
        {...(!!onHide && { onHide })}
        onBack={onBack}
        onSubmit={handleOnSubmit}
        onEditEntry={setEntryDraft}
        onToggleCreatingNewEntry={(bool) => setIsCreatingNewEntry(bool)}
      />
    );
  }

  return (
    <Panel
      width="560px"
      title={intl.formatMessage({
        id: 'TurnTrackingPanel.title',
        defaultMessage: 'Cycle Time Tracker',
        description: 'Panel title for cycle time tracker',
      })}
      mode={mode}
      {...(!!onHide && { hidden: { isHidden, onHide } })}
      {...(showInitiateResponsibleParty && {
        footer: {
          actions: [
            {
              text: intl.formatMessage({
                id: 'TurnTrackingPanel.setResponsiblePartyFooterButton',
                defaultMessage: 'Set Responsible Party',
                description: 'Footer button text to set responsible party',
              }),
              level: 'primary',
              onClick: handleOnSubmit,
              isLoading: isLoadingActionButton,
              disabled: !partyId,
            },
          ],
        },
      })}
    >
      {renderingLogic()}
    </Panel>
  );
};
